/**
 * LIVE translated captions — the worker's `translation_output` deltas,
 * accumulated into one rolling caption line PER SOURCE session.
 *
 * THE WIRE THIS CONSUMES, ESTABLISHED BY READING THE PRODUCER
 *
 *     `worker-translateaiz/translateaiz/caption_publisher.py` (CAPTION_TOPIC =
 *     'translation_output') sends each provider transcript delta as its OWN
 *     text stream, addressed per listener via `destination_identities`. There
 *     is NO envelope, NO turn identifier and NO terminal event — the producer
 *     documents that any segmentation on its side would be invented, so "the
 *     consumer accumulates". This module is that consumer.
 *
 * HOW THIS IS CONSUMED (2026-08-25 — the live-interim merge)
 *
 *     The accumulated rolling text renders as the interim CONTENT of the
 *     same speaker's PENDING transcript caption row (`CaptionOverlay`'s
 *     `liveInterimTextFor`), attributed via the verified roster and gated on
 *     the envelope's attested target language. It is never a caption row of
 *     its own — the dedicated rolling line was removed (owner, 2026-08-25:
 *     "You need to REMOVE duplicate code. Not just disable it."). This is
 *     also not the routes+revisions lineage join that settles the row: that
 *     path delivers the SETTLED reader text; this stream only ever fills the
 *     wait for it.
 *
 * WHY A MODULE STORE AND NOT THE REDUX SLICE
 *
 *     `translationSlice` is envelope- and lineage-driven; admitting raw
 *     unattributed deltas into it would give them a shape they do not have.
 *     A `useSyncExternalStore` module store (the `useCaptionPreference`
 *     idiom) keeps the accumulation local to the one surface that renders it.
 *
 * TURN BOUNDARIES: THE ENVELOPE'S TURN ID WHEN STAMPED, SILENCE OTHERWISE
 *
 *     The worker is adding an OPTIONAL `source_turn_id` to caption-delta.v1
 *     envelopes (consumer-first, 2026-08-25 — Sol's ruling against heuristic
 *     attachment). When consecutive deltas carry DIFFERENT turn ids, that IS
 *     a turn boundary: a new caption starts, so one rolling line can never
 *     fuse two turns' text under the last turn's id. For id-less deltas the
 *     old inference stands: a delta arriving more than
 *     `LIVE_TRANSLATION_TURN_GAP_MS` after the previous one starts a NEW
 *     caption (new id). That threshold deliberately equals the caption
 *     engine's LINE_TIMEOUT_MS (`components/conference/captionEngine.ts`): a
 *     row that has been retired on screen must never be silently mutated
 *     into carrying new text. The silence rule is a display policy, not a
 *     claim about turns — only the stamped id is.
 */

import { useSyncExternalStore } from 'react';

import logger from '@/lib/logger';
import { LAUNCH_LANGUAGE_CODES } from '@/lib/translation/launchLanguages';
import type { RevisionAttachmentV1 } from '@/lib/translation/revisionEnvelope';

const LAUNCH_LANGUAGE_CODE_SET: ReadonlySet<string> = new Set(LAUNCH_LANGUAGE_CODES);

/** The worker's caption topic (`caption_publisher.CAPTION_TOPIC`). */
export const LIVE_TRANSLATION_TOPIC = 'translation_output';

/**
 * Silence gap after which the next delta starts a new caption. Matches the
 * overlay's LINE_TIMEOUT_MS (CaptionOverlay.tsx) on purpose — see header.
 */
export const LIVE_TRANSLATION_TURN_GAP_MS = 6000;

export interface LiveTranslatedCaption {
  /** Stable per rolling line; changes only when a silence gap starts a new one. */
  id: string;
  /** Complete accumulated delta text for this rolling line. */
  text: string;
  /** Count of UTF-16 code units appended to this line, matching `text.length`. */
  total_chars: number;
  /** Wall-clock ms of the most recent delta (drives the overlay's timeout). */
  lastUpdateMs: number;
  /**
   * Wire envelope facts (2026-08-24, caption-delta.v1) — null on legacy raw
   * text from a worker older than the envelope. When present, a delta whose
   * source/target/epoch differs from the current caption's STARTS A NEW line:
   * the attribution/epoch keying the design review required before interim
   * suppression may rely on this surface.
   */
  target_language: string | null;
  source_language: string | null;
  source_participant_id: string | null;
  /**
   * OPTIONAL worker stamp (consumer-first): the turn this delta translates.
   * When present it is the AUTHORITATIVE attachment key — the overlay's
   * live-interim merge correlates it against the bubble's own turn ids and
   * only falls back to the spid/fence heuristics when it is null. Part of
   * the line KEY: an id change starts a new line.
   */
  source_turn_id: string | null;
  source_epoch: number | null;
  assignment_generation: number | null;
  route_generation: number | null;
  /**
   * Rule 120 / owner ruling D9 (2026-09-10): the words on this line were
   * RENDERED, and the stream that produced them has since been superseded (a
   * newer epoch or route for the same source, or a reader-language boundary).
   * The words are KEPT — never removed — and the surface may mark the row;
   * the island row proper is TL-01. A superseded line never grows again: a
   * delta for its key starts a new line.
   */
  superseded: boolean;
  /**
   * D3 (TL-05): the ABSOLUTE ENGINE OFFSET (code points) of this line's first
   * character on the worker's stream — the first delta's `engine_offset`. With
   * it, a settled revision is placed by its range and a delta from elsewhere
   * on the stream is a boundary, never a splice. Null from an offset-less
   * producer (the legacy text-search placement then runs, counted).
   */
  engine_offset: number | null;
}

// ════════════════════════════════════════════════════════════════════════════
// NEVER SILENT (rule 120, owner ruling D9). Every place this store used to
// drop engine words with a bare `return` now records WHAT it refused and WHY:
// a counter per reason and a bounded log of the refused text, plus a logger
// line carrying the words. Rendered words are never dropped at all — a
// superseded line is kept and marked (`superseded`), and the cap evicts a
// superseded line before a live one, recording the text it evicted.
// ════════════════════════════════════════════════════════════════════════════

export type LiveTranslationRefusalReason =
  | 'stale_epoch'
  | 'stale_route_generation'
  | 'stale_assignment_generation'
  | 'foreign_envelope_version'
  | 'cap_evicted'
  | 'display_boundary';

export interface LiveTranslationRefusal {
  readonly reason: LiveTranslationRefusalReason;
  /** The engine's words (or, for a foreign envelope, the raw payload head). */
  readonly text: string;
  readonly source_participant_id: string | null;
  readonly source_epoch: number | null;
  readonly target_language: string | null;
  readonly atMs: number;
}

const REFUSAL_REASONS: readonly LiveTranslationRefusalReason[] = [
  'stale_epoch',
  'stale_route_generation',
  'stale_assignment_generation',
  'foreign_envelope_version',
  'cap_evicted',
  'display_boundary',
];
const REFUSAL_LOG_CAP = 256;

function zeroRefusalCounts(): Record<LiveTranslationRefusalReason, number> {
  const counts = {} as Record<LiveTranslationRefusalReason, number>;
  for (const reason of REFUSAL_REASONS) counts[reason] = 0;
  return counts;
}

let refusalCounts = zeroRefusalCounts();
let refusalLog: LiveTranslationRefusal[] = [];

function refuse(
  reason: LiveTranslationRefusalReason,
  text: string,
  facts: {
    source_participant_id: string | null;
    source_epoch: number | null;
    target_language: string | null;
  },
  atMs: number,
): void {
  refusalCounts[reason] += 1;
  const entry: LiveTranslationRefusal = {
    reason,
    text,
    source_participant_id: facts.source_participant_id,
    source_epoch: facts.source_epoch,
    target_language: facts.target_language,
    atMs,
  };
  refusalLog.push(entry);
  if (refusalLog.length > REFUSAL_LOG_CAP) refusalLog = refusalLog.slice(-REFUSAL_LOG_CAP);
  // The words ride in the log line on purpose (rule 120): a refusal that
  // hides what it refused is a silent drop with a number attached.
  // A refusal that DROPS words is a failsafe firing — ERROR (owner rule
  // 2026-09-10). `display_boundary` keeps the words (marked superseded) and
  // fires on every reader-language switch, so it stays informational.
  const log = reason === 'display_boundary' ? logger.info : logger.error;
  log('liveTranslatedCaptions', `engine words refused: ${reason}`, {
    reason,
    text,
    source_participant_id: facts.source_participant_id,
    source_epoch: facts.source_epoch,
    target_language: facts.target_language,
    count: refusalCounts[reason],
  });
}

/** Per-reason counters since the last `resetLiveTranslatedCaption()`. */
export function getLiveTranslationRefusalCounts(): Readonly<
  Record<LiveTranslationRefusalReason, number>
> {
  return { ...refusalCounts };
}

/** The refused words, oldest first, bounded to the last REFUSAL_LOG_CAP. */
export function getLiveTranslationRefusals(): readonly LiveTranslationRefusal[] {
  return refusalLog.slice();
}

let current: LiveTranslatedCaption | null = null;
let currentLines: readonly LiveTranslatedCaption[] = Object.freeze([]);
let captionCounter = 0;
/**
 * One provisional line per attested stream key. A single module-global line
 * made alternating speakers erase each other: speaker B's first delta replaced
 * speaker A's still-visible provisional caption even though both transcript
 * bubbles remained on screen. The wire cannot truthfully provide turn ids in
 * the normal realtime path, so source/epoch/target is the narrowest honest key;
 * an optional source_turn_id further separates lines when a future producer can
 * attest one.
 */
const linesByStreamKey = new Map<string, LiveTranslatedCaption>();
const ACTIVE_LINE_CAP = 64;
/**
 * HIGH-WATER fences that survive line replacement (r3 finding 3): comparing
 * against only the CURRENT line forgets speaker A's epoch the moment speaker
 * B takes the line, so a delayed A stream slipped back in. Keyed per source;
 * bounded by room size and cap-guarded; reset with the store.
 */
const epochHighWaterBySource = new Map<string, number>();
const routeGenHighWater = new Map<string, number>();
// CALL-GLOBAL, not per-source (r4 finding 3): worker sessions stamp the
// call's assignment watermark, so a lower generation from ANY source is a
// superseded call world, not another speaker's legitimate snapshot.
let assignmentHighWater: number | null = null;
const FENCE_MAP_CAP = 64;
const listeners = new Set<() => void>();

function emit(): void {
  // Copied before iterating so a listener unsubscribing mid-notify is safe.
  for (const listener of Array.from(listeners)) listener();
}

export function subscribeLiveTranslatedCaption(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getLiveTranslatedCaptionSnapshot(): LiveTranslatedCaption | null {
  return current;
}

/** Every active provisional line, newest first. The array is reference-stable. */
export function getLiveTranslatedCaptionsSnapshot(): readonly LiveTranslatedCaption[] {
  return currentLines;
}

const EMPTY_SERVER_LINES: readonly LiveTranslatedCaption[] = Object.freeze([]);

/** SSR snapshot: no stream exists on the server, ever. */
function getServerLinesSnapshot(): readonly LiveTranslatedCaption[] {
  return EMPTY_SERVER_LINES;
}

/**
 * Append one wire delta. `nowMs` is injectable for tests only; every product
 * caller lets it default.
 *
 * Whitespace-only deltas are appended, not refused — the provider emits word
 * separators as their own deltas, and eating them would fuse words. Only the
 * EMPTY delta is a no-op, and a whitespace-only delta never STARTS a caption
 * (a caption of pure whitespace is nothing to display, and the overlay's
 * `text.trim()` filter would fight a line that exists but shows nothing).
 */
const CAPTION_DELTA_V1 = 'live-translation.caption-delta.v1';

interface ParsedCaptionDelta {
  text: string;
  /** True when the envelope's contract version is not ours (text salvaged or empty). */
  foreign_envelope: boolean;
  target_language: string | null;
  source_language: string | null;
  source_participant_id: string | null;
  source_turn_id: string | null;
  source_epoch: number | null;
  assignment_generation: number | null;
  route_generation: number | null;
  /** D3: the delta's absolute engine offset (code points), null when absent or malformed. */
  engine_offset: number | null;
}

/**
 * D3 (TL-05) counters. None of these drops words: a malformed offset degrades
 * to "no offset" (the words still render), a discontinuity starts a new line
 * (the old one is kept, superseded), and a legacy placement is the text
 * search running because the line has no origin. Each is a failsafe firing —
 * counted, and logged at ERROR by its site.
 */
export interface LiveTranslationOffsetCounts {
  offset_malformed: number;
  offset_discontinuity: number;
  legacy_text_placement: number;
}
let offsetCounts: LiveTranslationOffsetCounts = {
  offset_malformed: 0,
  offset_discontinuity: 0,
  legacy_text_placement: 0,
};
/** Rows whose legacy placement has already been counted (one count per row). */
const legacyPlacementCounted = new Set<string>();
const LEGACY_PLACEMENT_MEMORY_CAP = 512;

export function getLiveTranslationOffsetCounts(): Readonly<LiveTranslationOffsetCounts> {
  return { ...offsetCounts };
}

function streamKeyFor(delta: ParsedCaptionDelta): string {
  return [
    delta.target_language ?? '',
    delta.source_participant_id ?? '',
    delta.source_turn_id ?? '',
    delta.source_epoch === null ? '' : String(delta.source_epoch),
  ].join('\u0000');
}

function publishLineSnapshot(latest: LiveTranslatedCaption): void {
  current = latest;
  currentLines = Object.freeze(
    Array.from(linesByStreamKey.values()).sort(
      (left, right) =>
        right.lastUpdateMs - left.lastUpdateMs || right.id.localeCompare(left.id),
    ),
  );
}

const SUPERSEDED_KEY_TAG = '\u0000superseded\u0000';

/**
 * Mark one line superseded: its words stay, its stream key is released so the
 * next delta for that key starts a NEW line instead of growing a line whose
 * stream is over. The line is re-keyed under a private tag (unique per line
 * id), so it still counts against the cap and is evicted first.
 */
function supersedeLine(streamKey: string, line: LiveTranslatedCaption): void {
  if (line.superseded) return;
  linesByStreamKey.delete(streamKey);
  linesByStreamKey.set(`${streamKey}${SUPERSEDED_KEY_TAG}${line.id}`, {
    ...line,
    superseded: true,
  });
}

/**
 * A newer epoch (or a newer route at the same epoch) for the SAME source
 * supersedes that source's earlier lines. Their words are kept and marked.
 */
function supersedeEarlierStreamsOf(streamKey: string, line: LiveTranslatedCaption): void {
  const spid = line.source_participant_id;
  if (spid === null || line.source_epoch === null) return;
  const toSupersede: Array<[string, LiveTranslatedCaption]> = [];
  for (const [candidateKey, candidate] of linesByStreamKey) {
    if (candidateKey === streamKey || candidate.superseded) continue;
    if (candidate.source_participant_id !== spid || candidate.source_epoch === null) continue;
    const olderEpoch = candidate.source_epoch < line.source_epoch;
    const olderRoute =
      candidate.source_epoch === line.source_epoch &&
      candidate.route_generation !== null &&
      line.route_generation !== null &&
      candidate.route_generation < line.route_generation;
    if (olderEpoch || olderRoute) toSupersede.push([candidateKey, candidate]);
  }
  for (const [candidateKey, candidate] of toSupersede) supersedeLine(candidateKey, candidate);
}

function admitLine(streamKey: string, line: LiveTranslatedCaption, nowMs: number): void {
  linesByStreamKey.set(streamKey, line);
  supersedeEarlierStreamsOf(streamKey, line);
  if (linesByStreamKey.size > ACTIVE_LINE_CAP) {
    // Evict a SUPERSEDED line before a live one, oldest first within each
    // class — and record what left (rule 120: never silently).
    let victimKey: string | null = null;
    let victim: LiveTranslatedCaption | null = null;
    let victimRank = Number.POSITIVE_INFINITY;
    for (const [candidateKey, candidate] of linesByStreamKey) {
      if (candidateKey === streamKey) continue;
      const rank = (candidate.superseded ? 0 : Number.MAX_SAFE_INTEGER / 2) + candidate.lastUpdateMs;
      if (rank < victimRank) {
        victimRank = rank;
        victimKey = candidateKey;
        victim = candidate;
      }
    }
    if (victimKey !== null && victim !== null) {
      linesByStreamKey.delete(victimKey);
      refuse(
        'cap_evicted',
        victim.text,
        {
          source_participant_id: victim.source_participant_id,
          source_epoch: victim.source_epoch,
          target_language: victim.target_language,
        },
        nowMs,
      );
    }
  }
  publishLineSnapshot(line);
}

// ════════════════════════════════════════════════════════════════════════════
// NO RETRACTION — the settled slice and its provisional prefix + suffix (owner
// requirement 2026-09-02; sol design verdict on board run 23; surface-scoped
// on board run 24).
//
// Inside a live turn a caption row may only GROW. Once a durable revision
// lands it is a settled SLICE of the engine's rolling line — its head (run
// 23) or, when the worker attached the head to the previous turn as a
// corrective row, its middle or tail (run 24) — and the line's remainder on
// BOTH sides keeps rendering as provisional text. The worker
// cuts revisions at CHARACTER offsets on its absolute engine stream — run 23's
// own evidence is '...around six' | ', maybe a little after.', a cut inside a
// token — so the prefix rule is a whitespace-collapsed CODE-POINT prefix, not a
// token prefix (a token rule refused the very case it exists for).
//
// THE RANGE UNIT, PINNED: CODE POINTS. `engine_range_start/end` are Python
// `str` indices in the worker ledger; JavaScript strings are UTF-16, where
// '😀'.length === 2 but it is ONE engine character. Every offset in this
// section is counted with `Array.from` / code-point slicing, never
// `String#length` or `String#slice`.
//
// DEDUPE BY ABSOLUTE RANGE, NOT TOKENS (sol): settled revisions own their
// [start, end) intervals in their coordinate space (a joint-allocation group,
// else the ledger's absolute stream — `engineCoordinateSpaceKey`); the
// provisional text is the live range minus every settled range whose line is
// RENDERED on the surface. When a later row settles an interval an earlier row
// was showing as its suffix, the interval leaves the earlier row — rendered
// exactly once, never nowhere. An interval settled on a line that has RETIRED
// (run 24: the head the worker attached to the previous turn after its
// caption aged out) stays on the row that streamed it, as provisional text —
// exactly once ON THE SURFACE, which is the only place the officer reads.
// The live deltas carry NO absolute offset today, so the live line is
// anchored through the row's OWN settled revision: the revision's range names
// where its text sits on the stream, and the text's position in the line
// gives the line's origin (`liveBase = engine_range_start - slice.start`).
// A producer-contract inconsistency (an exact row the line still extends, an
// overlapping claim, a claim whose text is not the stream's slice, a range
// whose length is not its text's) is REPORTED to the caller and never
// suppresses the live words.
// ════════════════════════════════════════════════════════════════════════════

/** Length in CODE POINTS (the engine-stream unit), not UTF-16 units. */
export function codePointLength(text: string): number {
  let count = 0;
  for (const _ of text) count += 1;
  return count;
}

/** `text[start, end)` in CODE POINTS. Out-of-range bounds clamp; never throws. */
export function sliceCodePoints(text: string, start: number, end?: number): string {
  const points = Array.from(text);
  const from = Math.max(0, Math.min(start, points.length));
  const to = end === undefined ? points.length : Math.max(from, Math.min(end, points.length));
  return points.slice(from, to).join('');
}

const WHITESPACE = /\s/u;

function isWhitespace(point: string): boolean {
  return WHITESPACE.test(point);
}

/** Collapse a leading whitespace run to ONE space; no other change. */
function collapseLeadingWhitespace(points: readonly string[]): string {
  let index = 0;
  while (index < points.length && isWhitespace(points[index])) index += 1;
  return (index > 0 ? ' ' : '') + points.slice(index).join('');
}

export interface LiveTranslationRemainder {
  /**
   * The rolling line beyond the settled prefix, its leading whitespace run
   * collapsed to one space so it joins the settled text the way the engine
   * wrote it (', maybe' joins directly; ' me to add' joins with a space).
   */
  readonly text: string;
  /** Code-point offset into the ROLLING text where the remainder begins. */
  readonly offset: number;
}

/**
 * The rolling line's remainder after the settled text, or `null` when the
 * settled text is not a whitespace-collapsed code-point prefix of it — or
 * equals it (equal text is not a STRICT extension; nothing to keep).
 *
 * Words and punctuation must match code point for code point; only the
 * LENGTH of a whitespace run may differ (per-turn revisions are joined with
 * presentation whitespace). A different wording returns `null` rather than a
 * guess — the row keeps its settled text and no suffix.
 */
export function deriveLiveTranslationRemainder(
  settledText: string,
  rollingText: string,
): LiveTranslationRemainder | null {
  const settled = Array.from(settledText);
  const rolling = Array.from(rollingText);
  let i = 0;
  let j = 0;
  while (i < settled.length && isWhitespace(settled[i])) i += 1;
  while (j < rolling.length && isWhitespace(rolling[j])) j += 1;
  let settledEnd = settled.length;
  while (settledEnd > i && isWhitespace(settled[settledEnd - 1])) settledEnd -= 1;

  while (i < settledEnd) {
    if (isWhitespace(settled[i])) {
      if (j >= rolling.length || !isWhitespace(rolling[j])) return null;
      while (i < settledEnd && isWhitespace(settled[i])) i += 1;
      while (j < rolling.length && isWhitespace(rolling[j])) j += 1;
      continue;
    }
    if (j >= rolling.length || settled[i] !== rolling[j]) return null;
    i += 1;
    j += 1;
  }

  const rest = rolling.slice(j);
  if (rest.every(isWhitespace)) return null;
  return { text: collapseLeadingWhitespace(rest), offset: j };
}

/**
 * The trimmed remainder — the legacy helper, kept for callers that only need
 * the words. It now agrees with the code-point rule on a mid-token cut.
 */
export function deriveLiveTranslationSuffix(
  settledText: string,
  rollingText: string,
): string | null {
  const remainder = deriveLiveTranslationRemainder(settledText, rollingText);
  if (remainder === null) return null;
  const suffix = remainder.text.trim();
  return suffix || null;
}

/** One SELECTED revision's settled interval — the dedupe registry's entry. */
export interface SettledEngineRange {
  readonly source_turn_id: string;
  readonly target_language: string;
  readonly translation_revision_id: string;
  readonly revision_index: number;
  /**
   * The worker's joint-allocation group, or `null` — the wire's ORDINARY
   * value (board run 24): `revision_writer.py` mints a group id only for a
   * joint (multi-member) allocation; every single-member commit and every
   * corrective row carries null. Null-group rows share the ledger's ABSOLUTE
   * engine-stream coordinates — see {@link engineCoordinateSpaceKey}.
   */
  readonly allocation_group_id: string | null;
  /** Half-open [start, end), absolute code points within the coordinate space. */
  readonly start: number;
  readonly end: number;
  /** The revision's text — must equal the engine stream's slice for its range. */
  readonly text: string;
  /** Available, validated stream coordinates; absent on older stored lineage. */
  readonly engine_stream_key?: string;
}

export interface CaptionEngineStreamCoordinates {
  readonly call_id: string | null;
  readonly stable_participant_id: string | null;
  readonly source_epoch: number | null;
  readonly source_track_sid: string | null;
  readonly target_language: string | null;
  readonly assignment_generation: number | null;
  readonly route_generation: number | null;
}

/**
 * Observed coordinates for cross-allocation caption claims. The writer uses
 * ABSOLUTE offsets even when allocation_group_id is non-null
 * (revision_writer.py: part_start = range_start + offsets[index]). A group
 * identifies one allocation, not a new stream origin.
 *
 * The ledger also keys on translation_session_record_id. That internal ID is
 * NOT in the browser contract; translation_session_id is a different field
 * and the writer sends it as null. Do not manufacture the missing identity.
 * These are the seven coordinates we do observe. Exact slice agreement and
 * overlap checks below remain mandatory; uncertain claims never erase words.
 */
export function captionEngineStreamKey(input: CaptionEngineStreamCoordinates): string | null {
  const strings = [
    input.call_id,
    input.stable_participant_id,
    input.source_track_sid,
    input.target_language,
  ];
  const generations = [input.source_epoch, input.assignment_generation, input.route_generation];
  if (
    strings.some((value) => typeof value !== 'string' || value.length === 0) ||
    generations.some((value) => value === null || !Number.isSafeInteger(value) || value < 0)
  )
    return null;
  return JSON.stringify([
    'caption-engine-stream',
    input.call_id,
    input.stable_participant_id,
    input.source_epoch,
    input.source_track_sid,
    input.target_language,
    input.assignment_generation,
    input.route_generation,
  ]);
}

export interface EngineCoordinateSpaceInput {
  readonly allocation_group_id: string | null;
  readonly target_language: string | null;
  readonly stable_participant_id: string | null;
  readonly source_epoch: number | null;
}

/**
 * Legacy registry bucket (run 24), retained for stored lineage without the
 * observed stream coordinates above. A joint allocation gets its own bucket;
 * its ranges still use ABSOLUTE offsets, just like null-group rows. The worker
 * ledger is keyed by (call, translation session,
 * source participant, source epoch, source track); the browser sees the
 * target language, the source participant and the source epoch of those, so
 * that triple is the space — the same key the live line itself is kept
 * under (`streamKeyFor`). Two rows of one speaker's one epoch, one target,
 * are therefore comparable by range; a re-published track (new epoch) is a
 * new stream and never collides.
 */
export function engineCoordinateSpaceKey(input: EngineCoordinateSpaceInput): string {
  // A group id is a uuid and can never collide with the absolute key below.
  if (input.allocation_group_id !== null) return input.allocation_group_id;
  return [
    'absolute',
    input.target_language ?? '',
    input.stable_participant_id ?? '',
    input.source_epoch === null ? '' : String(input.source_epoch),
  ].join('\u0000');
}

export type LiveSuffixConflictKind =
  /**
   * D3: the row's engine range lies outside the live line's known extent
   * (the line has an origin and the range is before or beyond it). The row is
   * placed by text as before; the disagreement is reported, never hidden.
   */
  | 'range_outside_live_line'
  /**
   * `attachment_confidence === 'exact'`, yet the live line extends the row
   * with words NO settled claim accounts for. An extension a claim covers —
   * the head the worker re-attached to the previous turn as a corrective row
   * — is the producer's designed re-segmentation, not a conflict.
   */
  | 'exact_row_extended_by_live'
  /** A settled claim's text is not the live stream's slice at its range. */
  | 'settled_claim_text_mismatch'
  /** Two selected revisions in one coordinate space claim overlapping ranges. */
  | 'settled_range_overlap'
  /** The row's own range length is not its text's code-point length. */
  | 'range_length_mismatch'
  /** Every placement of the settled text puts the live line's origin before absolute 0. */
  | 'range_before_live_origin';

export interface LiveSuffixConflict {
  readonly kind: LiveSuffixConflictKind;
  /** Which revision(s) the conflict is about, for the lamp. */
  readonly revision_ids: readonly string[];
  readonly detail: string;
}

export interface LiveSuffixReconciliation {
  /**
   * What renders BEFORE the settled text (run 24): the live line's remainder
   * ahead of the settled slice, minus every interval a RENDERED claim owns.
   * `null` when nothing is owed.
   */
  readonly prefix: string | null;
  /** What renders behind the settled text; `null` when nothing is owed. */
  readonly suffix: string | null;
  /** True iff a prefix or a suffix exists: the live line strictly extends the row. */
  readonly extendsSettled: boolean;
  /** `by_range` when the row is anchored on the engine stream; else by text only. */
  readonly dedupe: 'by_range' | 'unavailable';
  readonly conflicts: readonly LiveSuffixConflict[];
}

export interface ReconcileLiveSuffixInput {
  /** The row's settled reader text (the selected revision, or the bubble's join). */
  readonly settledText: string;
  /** The engine's rolling line attached to this row. */
  readonly rollingText: string;
  /** The row's selected revision attachment; `null` for a legacy 19-key row. */
  readonly attachment: RevisionAttachmentV1 | null;
  /** Every SELECTED range in the row's coordinate space (own row included). */
  readonly settledRanges: readonly SettledEngineRange[];
  /** The row's selected revision id, so its own range is never a "claim". */
  readonly ownRevisionId: string | null;
  /**
   * When present, compare selected claims by observed stream coordinates
   * across allocation groups. Otherwise retain historical group semantics.
   */
  readonly engineStreamKey?: string;
  /**
   * D3 (TL-05): the live line's absolute engine offset (its first character's
   * position on the worker's stream, code points). When present, a settled
   * row is placed by its range alone; when absent, the legacy text search
   * places it and is counted (`legacy_text_placement`).
   */
  readonly rollingEngineOffset?: number | null;
  /**
   * SURFACE SCOPE (run 24): is this claim's line rendered on the caption
   * surface right now (a line mid-exit included)? Only a RENDERED claim
   * removes its interval from this row's provisional text — that line shows
   * the words. A claim whose line has RETIRED still ACCOUNTS for its interval
   * (it is attachment truth, so an exact row is not in conflict) but its
   * words stay HERE, on the row that streamed them: each interval renders
   * exactly once ON THE SURFACE, never nowhere. Default: every claim is
   * rendered (the store-wide rule).
   */
  readonly isRenderedOnSurface?: (claim: SettledEngineRange) => boolean;
  /**
   * NEIGHBOURS (the overlay's second pass): the settled slices, in
   * `rollingText` code points, of the OTHER rendered rows anchored on this
   * same rolling line. An unclaimed interval between two anchored rows is the
   * EARLIER row's suffix, through the extent that row can actually supply.
   * Any remaining prefix stays on this row. Its suffix ends where the next
   * neighbour's slice begins. Legacy two-field callers claim the whole prefix.
   */
  readonly neighbourSlices?: readonly LiveNeighbourSlice[];
  /**
   * D4c (TL-02, rule 121): the row's anchor turn is SETTLED (`final_attached`
   * on the wire) and nothing follows it in the row. The live line is CUT at
   * the settled end (design-d4 §3): nothing behind the settled slice renders
   * on this row and it is not an extension of it — the words behind a
   * settled row are the NEXT turn's line. They stay in the live store and
   * render on the row of the turn they belong to; a tail the worker cannot
   * attach to a next turn is a `late_after_settlement` island (rule 120).
   * The words AHEAD of the slice are untouched.
   */
  readonly cutAtSettledEnd?: boolean;
}

/** The reconciliation with its provisional text as code-point pieces of the rolling text. */
export interface LiveSuffixPieces extends LiveSuffixReconciliation {
  /** Where the settled text sits in `rollingText`, [start, end); `null` when it is not a slice. */
  readonly settledSlice: readonly [number, number] | null;
  /** Half-open [start, end) code-point intervals of `rollingText` before the slice, ascending. */
  readonly prefixPieces: readonly (readonly [number, number])[];
  /** Half-open [start, end) code-point intervals of `rollingText` after the slice, ascending. */
  readonly suffixPieces: readonly (readonly [number, number])[];
  /** `prefixPieces` then `suffixPieces` — every provisional interval, ascending. */
  readonly pieces: readonly (readonly [number, number])[];
}

/**
 * Join suffix pieces of the rolling text into the rendered suffix, optionally
 * clipped to the first `limit` code points of the line (the typewriter's
 * painted prefix). The first piece's leading whitespace collapses to one
 * space; non-adjacent pieces are separated by one space so words never fuse.
 */
export function joinLiveSuffixPieces(
  rollingText: string,
  pieces: readonly (readonly [number, number])[],
  limit: number = Number.POSITIVE_INFINITY,
): string | null {
  const points = Array.from(rollingText);
  let out: string[] = [];
  let previousEnd: number | null = null;
  for (const [start, end] of pieces) {
    const to = Math.min(end, limit, points.length);
    if (to <= start) continue;
    const piece = points.slice(start, to);
    if (previousEnd === null) {
      out = [collapseLeadingWhitespace(piece)];
    } else {
      const last = out[out.length - 1];
      const needsSpace =
        previousEnd !== start &&
        last.length > 0 &&
        !isWhitespace(last[last.length - 1]) &&
        !isWhitespace(piece[0]);
      out.push((needsSpace ? ' ' : '') + piece.join(''));
    }
    previousEnd = to;
  }
  const suffix = out.join('');
  return suffix.trim().length > 0 ? suffix : null;
}

/**
 * Join PREFIX pieces (run 24): as {@link joinLiveSuffixPieces}, but the
 * result begins the row, so its leading whitespace is dropped, and its
 * trailing whitespace run — the join to the settled text — collapses to one
 * space (the settled text carries its own leading space when the worker cut
 * before one, so "Hello … I'm" + " still waiting" joins exactly as streamed).
 */
export function joinLivePrefixPieces(
  rollingText: string,
  pieces: readonly (readonly [number, number])[],
  limit: number = Number.POSITIVE_INFINITY,
): string | null {
  const joined = joinLiveSuffixPieces(rollingText, pieces, limit);
  if (joined === null) return null;
  return joined.replace(/^\s+/u, '').replace(/\s+$/u, ' ');
}

/** Neighbour anchor plus the last code point its row can actually supply. */
export type LiveNeighbourSlice = readonly [start: number, end: number, coveredThrough?: number];

/** One occurrence of the settled text inside the rolling text, in rolling code points. */
export interface SettledSliceMatch {
  /** Where the match begins — the settled text's own leading whitespace run included. */
  readonly start: number;
  /** Just past the match — the settled text's own trailing whitespace run included. */
  readonly end: number;
}

/**
 * Every position at which `settledText` occurs in `rollingText` as a
 * whitespace-collapsed CODE-POINT slice, ascending (run 24: the settled text
 * may be the MIDDLE or the TAIL of the line, not only its head).
 *
 * Words and punctuation match code point for code point; a whitespace run
 * inside the settled text matches a whitespace run of any positive length;
 * the settled text's own leading and trailing runs match a possibly-empty
 * run, so ' still waiting' found after "I'm" claims the space before it. A
 * match may begin or end inside a token — the worker cuts at character
 * offsets. Empty or whitespace-only settled text matches nowhere.
 */
export function locateSettledSlices(settledText: string, rollingText: string): SettledSliceMatch[] {
  const settled = Array.from(settledText);
  const rolling = Array.from(rollingText);
  let first = 0;
  while (first < settled.length && isWhitespace(settled[first])) first += 1;
  let last = settled.length;
  while (last > first && isWhitespace(settled[last - 1])) last -= 1;
  if (first >= last) return [];
  const leadingRun = first > 0;
  const trailingRun = last < settled.length;

  const matches: SettledSliceMatch[] = [];
  for (let position = 0; position < rolling.length; position += 1) {
    if (rolling[position] !== settled[first]) continue;
    let i = first;
    let j = position;
    let matched = true;
    while (i < last) {
      if (isWhitespace(settled[i])) {
        if (j >= rolling.length || !isWhitespace(rolling[j])) {
          matched = false;
          break;
        }
        while (i < last && isWhitespace(settled[i])) i += 1;
        while (j < rolling.length && isWhitespace(rolling[j])) j += 1;
        continue;
      }
      if (j >= rolling.length || settled[i] !== rolling[j]) {
        matched = false;
        break;
      }
      i += 1;
      j += 1;
    }
    if (!matched) continue;
    let start = position;
    if (leadingRun) while (start > 0 && isWhitespace(rolling[start - 1])) start -= 1;
    let end = j;
    if (trailingRun) while (end < rolling.length && isWhitespace(rolling[end])) end += 1;
    matches.push({ start, end });
  }
  return matches;
}

function isAnchored(
  attachment: RevisionAttachmentV1 | null,
): attachment is RevisionAttachmentV1 & {
  engine_range_start: number;
  engine_range_end: number;
} {
  // A null group is the wire's ordinary row (run 24) — the RANGE anchors it.
  return (
    attachment !== null &&
    attachment.engine_range_start !== null &&
    attachment.engine_range_end !== null
  );
}

/** Subtract [lo, hi) from every interval in `pieces` (all half-open). */
function subtractInterval(
  pieces: readonly (readonly [number, number])[],
  lo: number,
  hi: number,
): (readonly [number, number])[] {
  const next: (readonly [number, number])[] = [];
  for (const [start, end] of pieces) {
    if (hi <= start || lo >= end) {
      next.push([start, end]);
      continue;
    }
    if (lo > start) next.push([start, lo]);
    if (hi < end) next.push([hi, end]);
  }
  return next;
}

function isPrefixMatch(rolling: readonly string[], match: SettledSliceMatch): boolean {
  for (let index = 0; index < match.start; index += 1) {
    if (!isWhitespace(rolling[index])) return false;
  }
  return true;
}

/**
 * The provisional intervals around one placement of the settled text, before
 * any claim is subtracted: everything ahead of the slice (the PREFIX) and
 * everything behind it (the SUFFIX), each surrendered to a neighbouring
 * anchored row on the same line when one exists on that side.
 */
function provisionalAround(
  slice: SettledSliceMatch,
  rollingLength: number,
  neighbours: readonly LiveNeighbourSlice[],
): {
  prefix: (readonly [number, number])[];
  suffix: (readonly [number, number])[];
} {
  // A retained older row may have only a shorter snapshot of this original
  // buffer. It owns the prefix only through words it can actually supply;
  // newly received remainder stays here instead of disappearing between rows.
  let prefixStart = 0;
  for (const [, end, coveredThrough] of neighbours) {
    if (end <= slice.start) prefixStart = Math.max(prefixStart,
      Math.min(slice.start, coveredThrough === undefined ? slice.start : Math.max(end, coveredThrough)));
  }
  let nextNeighbourStart = rollingLength;
  for (const [start] of neighbours) {
    if (start >= slice.end && start < nextNeighbourStart) nextNeighbourStart = start;
  }
  return {
    prefix: prefixStart >= slice.start ? [] : [[prefixStart, slice.start]],
    suffix: nextNeighbourStart <= slice.end ? [] : [[slice.end, nextNeighbourStart]],
  };
}

const EMPTY_PIECES: readonly (readonly [number, number])[] = Object.freeze([]);

function nothingOwed(
  dedupe: 'by_range' | 'unavailable',
  conflicts: readonly LiveSuffixConflict[],
): LiveSuffixPieces {
  return {
    prefix: null,
    suffix: null,
    extendsSettled: false,
    dedupe,
    conflicts,
    settledSlice: null,
    prefixPieces: EMPTY_PIECES,
    suffixPieces: EMPTY_PIECES,
    pieces: EMPTY_PIECES,
  };
}

/** Code points of `rollingText` covered by `pieces`. */
function countCodePoints(
  rollingText: string,
  pieces: readonly (readonly [number, number])[],
): number {
  const length = codePointLength(rollingText);
  let count = 0;
  for (const [start, end] of pieces) {
    count += Math.max(0, Math.min(end, length) - Math.max(0, start));
  }
  return count;
}

/**
 * The reconciliation as pieces — what the overlay renders (and clips to the
 * typewriter's painted prefix). See {@link reconcileLiveSuffix}.
 */
export function reconcileLiveSuffixPieces(input: ReconcileLiveSuffixInput): LiveSuffixPieces {
  const { settledText, rollingText, attachment, settledRanges, ownRevisionId } = input;
  const isRendered = input.isRenderedOnSurface ?? (() => true);
  const neighbours = input.neighbourSlices ?? EMPTY_PIECES;
  const conflicts: LiveSuffixConflict[] = [];
  const rolling = Array.from(rollingText);
  const rollingLength = rolling.length;
  const matches = locateSettledSlices(settledText, rollingText);
  const exact = attachment !== null && attachment.attachment_confidence === 'exact';
  const cut = input.cutAtSettledEnd === true;
  const ownIds = ownRevisionId === null ? [] : [ownRevisionId];

  const finish = (
    slice: SettledSliceMatch,
    prefixPieces: readonly (readonly [number, number])[],
    suffixPieces: readonly (readonly [number, number])[],
    dedupe: 'by_range' | 'unavailable',
  ): LiveSuffixPieces => {
    const prefix = joinLivePrefixPieces(rollingText, prefixPieces);
    const suffix = joinLiveSuffixPieces(rollingText, suffixPieces);
    return {
      prefix,
      suffix,
      extendsSettled: prefix !== null || suffix !== null,
      dedupe,
      conflicts,
      settledSlice: [slice.start, slice.end],
      prefixPieces,
      suffixPieces,
      pieces: [...prefixPieces, ...suffixPieces],
    };
  };

  const reportExactExtension = (unaccounted: readonly (readonly [number, number])[]) => {
    if (!exact || joinLiveSuffixPieces(rollingText, unaccounted) === null) return;
    // 'exact' must never authorize retraction: the words still render, and
    // the unattached extension is reported — a corrective row is owed.
    conflicts.push({
      kind: 'exact_row_extended_by_live',
      revision_ids: ownIds,
      detail: `an exact revision is extended by ${countCodePoints(rollingText, unaccounted)} live code points no settled claim accounts for`,
    });
  };

  /** The head placement when one exists, else the last — for a row with no usable range. */
  const placeByText = (): SettledSliceMatch | null => {
    if (matches.length === 0) return null;
    return matches.find((match) => isPrefixMatch(rolling, match)) ?? matches[matches.length - 1];
  };

  if (!isAnchored(attachment)) {
    // A legacy row or a fallback row (null range) has no position on the
    // engine stream: the provisional text is by text alone, nothing can
    // account for it, and the caller is told dedupe is unavailable.
    const slice = placeByText();
    if (slice === null) return nothingOwed('unavailable', conflicts);
    const around = provisionalAround(slice, rollingLength, neighbours);
    const prefix = around.prefix;
    const suffix = cut ? EMPTY_PIECES : around.suffix;
    reportExactExtension([...prefix, ...suffix]);
    return finish(slice, prefix, suffix, 'unavailable');
  }

  const ownStart = attachment.engine_range_start;
  const ownEnd = attachment.engine_range_end;
  const ownLength = codePointLength(settledText);
  let anchorReliable = true;
  if (ownEnd - ownStart !== ownLength) {
    anchorReliable = false;
    conflicts.push({
      kind: 'range_length_mismatch',
      revision_ids: ownIds,
      detail: `range [${ownStart}, ${ownEnd}) spans ${ownEnd - ownStart} code points but the text has ${ownLength}`,
    });
  }
  if (matches.length === 0) return nothingOwed('by_range', conflicts);

  // Modern stored lineage identifies the observed stream across allocations.
  // Legacy callers/claims keep the prior same-group rule. Missing coordinates
  // cannot authorize subtraction from a known stream.
  const others = settledRanges.filter(
    (range) =>
      (input.engineStreamKey !== undefined
        ? range.engine_stream_key === input.engineStreamKey
        : range.allocation_group_id === attachment.allocation_group_id) &&
      range.translation_revision_id !== ownRevisionId,
  );

  /**
   * A claim's text against the stream over the part of it the live window
   * sees: the agreed [lo, hi) in absolute coordinates, `false` on a
   * mismatch, `null` when the claim lies outside the window.
   */
  const claimAgreesWithStream = (
    claim: SettledEngineRange,
    liveBase: number,
    liveEnd: number,
  ): readonly [number, number] | false | null => {
    const lo = Math.max(claim.start, liveBase);
    const hi = Math.min(claim.end, liveEnd);
    if (lo >= hi) return null;
    const streamSlice = sliceCodePoints(rollingText, lo - liveBase, hi - liveBase);
    const claimedSlice = sliceCodePoints(claim.text, lo - claim.start, hi - claim.start);
    return streamSlice === claimedSlice ? [lo, hi] : false;
  };

  // PLACEMENT (run 24): a settled text that occurs more than once in the
  // line is placed by its RANGE — the candidate the other claims corroborate
  // (their text is the stream's slice under that origin) wins; ties go to
  // the head placement, else the last. A placement that would put the live
  // line's origin before absolute 0 is impossible.
  let slice: SettledSliceMatch | null = null;
  // D3 (TL-05): the line knows where it starts on the stream — the row's range
  // IS its place on the line. No text search, no corroboration vote.
  const rollingOrigin = input.rollingEngineOffset ?? null;
  if (anchorReliable && rollingOrigin !== null) {
    const start = ownStart - rollingOrigin;
    const end = ownEnd - rollingOrigin;
    if (start >= 0 && end <= rollingLength && start < end) {
      slice = { start, end };
    } else {
      conflicts.push({
        kind: 'range_outside_live_line',
        revision_ids: ownIds,
        detail: `range [${ownStart}, ${ownEnd}) lies outside the live line [${rollingOrigin}, ${rollingOrigin + rollingLength})`,
      });
    }
  }
  if (slice === null && anchorReliable && rollingOrigin === null && ownRevisionId !== null) {
    // The legacy text placement is about to run: the line has no origin
    // (an offset-less producer). A failsafe, counted once per row at ERROR.
    if (!legacyPlacementCounted.has(ownRevisionId)) {
      if (legacyPlacementCounted.size >= LEGACY_PLACEMENT_MEMORY_CAP) legacyPlacementCounted.clear();
      legacyPlacementCounted.add(ownRevisionId);
      offsetCounts.legacy_text_placement += 1;
      logger.error('liveTranslatedCaptions', 'settled row placed by TEXT SEARCH: the live line has no engine offset', {
        revision_id: ownRevisionId,
        range: [ownStart, ownEnd],
        count: offsetCounts.legacy_text_placement,
      });
    }
  }
  if (slice === null && anchorReliable) {
    let bestSupport = -1;
    for (const match of matches) {
      const liveBase = ownStart - match.start;
      if (liveBase < 0) continue;
      const liveEnd = liveBase + rollingLength;
      let support = 0;
      for (const claim of others) {
        if (claimAgreesWithStream(claim, liveBase, liveEnd)) support += 1;
      }
      const tiedWithNonHead =
        support === bestSupport && slice !== null && !isPrefixMatch(rolling, slice);
      if (support > bestSupport || tiedWithNonHead) {
        bestSupport = support;
        slice = match;
      }
    }
    if (slice === null) {
      anchorReliable = false;
      conflicts.push({
        kind: 'range_before_live_origin',
        revision_ids: ownIds,
        detail: `range [${ownStart}, ${ownEnd}) places every occurrence of the text before the stream's origin`,
      });
    }
  }
  if (slice === null) slice = placeByText() as SettledSliceMatch;

  const around = provisionalAround(slice, rollingLength, neighbours);
  let prefixPieces: readonly (readonly [number, number])[] = around.prefix;
  // D4c: behind a settled row is the next turn's line, never this row's tail.
  let suffixPieces: readonly (readonly [number, number])[] = cut ? EMPTY_PIECES : around.suffix;
  // What no settled claim accounts for — an exact row's honest extension.
  let unaccounted: readonly (readonly [number, number])[] = [...prefixPieces, ...suffixPieces];

  if (anchorReliable) {
    const liveBase = ownStart - slice.start;
    const liveEnd = liveBase + rollingLength;
    const inWindow = others.filter((claim) => claim.start < liveEnd && claim.end > liveBase);
    // Overlapping claims inside the window are a producer defect: neither
    // claim may subtract live words (the overlap is reported, the words
    // survive). The row's own range takes part, as a claim would.
    const own: SettledEngineRange = {
      source_turn_id: '',
      target_language: '',
      translation_revision_id: ownRevisionId ?? '',
      revision_index: -1,
      allocation_group_id: attachment.allocation_group_id,
      start: ownStart,
      end: ownEnd,
      text: settledText,
    };
    const scan = [own, ...inWindow];
    const conflicted = new Set<string>();
    for (let a = 0; a < scan.length; a += 1) {
      for (let b = a + 1; b < scan.length; b += 1) {
        const left = scan[a];
        const right = scan[b];
        if (left.start < right.end && right.start < left.end) {
          conflicted.add(left.translation_revision_id);
          conflicted.add(right.translation_revision_id);
          conflicts.push({
            kind: 'settled_range_overlap',
            revision_ids: [left.translation_revision_id, right.translation_revision_id],
            detail: `[${left.start}, ${left.end}) overlaps [${right.start}, ${right.end})`,
          });
        }
      }
    }
    for (const claim of inWindow) {
      if (conflicted.has(claim.translation_revision_id)) continue;
      const agreed = claimAgreesWithStream(claim, liveBase, liveEnd);
      if (agreed === null) continue;
      if (agreed === false) {
        // The claim's text must be the stream's exact slice for its range;
        // a mismatch is RED and the claim neither subtracts nor accounts.
        conflicts.push({
          kind: 'settled_claim_text_mismatch',
          revision_ids: [claim.translation_revision_id],
          detail: `the claim for [${claim.start}, ${claim.end}) is not the engine stream's slice`,
        });
        continue;
      }
      const [lo, hi] = agreed;
      unaccounted = subtractInterval(unaccounted, lo - liveBase, hi - liveBase);
      // SURFACE SCOPE: only a claim whose line is rendered takes its words
      // off this row; a retired line's words stay here, exactly once.
      if (!isRendered(claim)) continue;
      prefixPieces = subtractInterval(prefixPieces, lo - liveBase, hi - liveBase);
      suffixPieces = subtractInterval(suffixPieces, lo - liveBase, hi - liveBase);
    }
  }

  reportExactExtension(unaccounted);
  return finish(slice, prefixPieces, suffixPieces, 'by_range');
}

/**
 * The provisional text for one caption row: the live line minus its settled
 * slice, minus every interval another SELECTED revision in the same
 * coordinate space has settled AND renders on the surface — each engine
 * interval rendered exactly once on the surface, never nowhere.
 * Conflicts are returned, never thrown, and never suppress the live words.
 */
export function reconcileLiveSuffix(input: ReconcileLiveSuffixInput): LiveSuffixReconciliation {
  const { prefix, suffix, extendsSettled, dedupe, conflicts } = reconcileLiveSuffixPieces(input);
  return { prefix, suffix, extendsSettled, dedupe, conflicts };
}

/**
 * TOLERANT wire ingest (2026-08-24): the worker's caption stream is upgrading
 * from raw text deltas to a caption-delta.v1 JSON envelope carrying the
 * source/target/generation facts the polarity work requires. This consumer
 * accepts BOTH — it deploys before the emitting worker, and a raw string that
 * merely LOOKS like JSON but is not the envelope stays what it always was:
 * caption text. Malformed envelope fields degrade to null, never to a dropped
 * delta — the words outrank the metadata.
 */
function parseCaptionDelta(delta: string): ParsedCaptionDelta {
  const legacy: ParsedCaptionDelta = {
    text: delta,
    target_language: null,
    source_language: null,
    source_participant_id: null,
    source_turn_id: null,
    source_epoch: null,
    assignment_generation: null,
    route_generation: null,
    engine_offset: null,
    foreign_envelope: false,
  };
  if (delta[0] !== '{') return legacy;
  let parsed: unknown;
  try {
    parsed = JSON.parse(delta);
  } catch {
    return legacy;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return legacy;
  const raw = parsed as Record<string, unknown>;
  if (raw.contract_version !== CAPTION_DELTA_V1) {
    // A DIFFERENT envelope version (or a JSON object that merely looks like
    // one) must never render as caption text — `{"contract_version":...}`
    // on an emergency caption surface is gibberish where words were owed
    // (foundations review r1, finding 8). Salvage the human text when the
    // object carries one; otherwise drop the delta entirely (empty text is a
    // no-op upstream). Only a NON-JSON string stays the legacy raw-text path.
    return {
      ...legacy,
      text: typeof raw.text === 'string' ? raw.text : '',
      foreign_envelope: true,
    };
  }
  if (typeof raw.text !== 'string') return { ...legacy, text: '' };
  // CONTRACT-checked, not merely type-checked (finding 7): languages must be
  // launch catalog codes, epochs/generations non-negative safe integers —
  // provider junk degrades to null fact-by-fact, never to a dropped delta
  // (the words outrank the metadata) and never into the store as fake lineage.
  const lang = (value: unknown): string | null =>
    typeof value === 'string' && LAUNCH_LANGUAGE_CODE_SET.has(value) ? value : null;
  const gen = (value: unknown): number | null =>
    typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
  const str = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);
  const assignmentGeneration = gen(raw.assignment_generation);
  // D3: optional; a present-but-malformed offset is a contract defect the
  // producer must hear about — counted at ERROR, degraded to null, the words
  // still render.
  let engineOffset: number | null = null;
  if ('engine_offset' in raw && raw.engine_offset !== undefined) {
    engineOffset = gen(raw.engine_offset);
    if (engineOffset === null) {
      offsetCounts.offset_malformed += 1;
      logger.error('liveTranslatedCaptions', 'caption delta carries a malformed engine_offset', {
        engine_offset: raw.engine_offset,
        text: raw.text,
        count: offsetCounts.offset_malformed,
      });
    }
  }
  return {
    text: raw.text,
    target_language: lang(raw.target_language),
    // BI-08: a source-language CLAIM without an assignment generation behind
    // it is not admissible — the claim degrades to null when the generation
    // did not survive validation.
    source_language: assignmentGeneration === null ? null : lang(raw.source_language),
    source_participant_id: str(raw.source_participant_id),
    // OPTIONAL (consumer-first): tolerated absent, degraded to null on junk.
    source_turn_id: str(raw.source_turn_id),
    source_epoch: gen(raw.source_epoch),
    assignment_generation: assignmentGeneration,
    route_generation: gen(raw.route_generation),
    engine_offset: engineOffset,
    foreign_envelope: false,
  };
}

const NO_FACTS = { source_participant_id: null, source_epoch: null, target_language: null };

export function appendLiveTranslationDelta(delta: string, nowMs: number = Date.now()): void {
  if (typeof delta !== 'string' || delta.length === 0) return;
  const parsed = parseCaptionDelta(delta);
  if (parsed.text.length === 0) {
    if (parsed.foreign_envelope) {
      // A version we do not speak and no human text to salvage: the payload
      // head is what we can preserve of it.
      refuse('foreign_envelope_version', delta.slice(0, 200), NO_FACTS, nowMs);
    }
    return;
  }
  if (parsed.foreign_envelope) {
    // Salvaged text from a version we do not speak lands as a legacy line
    // with no attested target — a line the overlay's attested-target guard
    // never renders. Recorded so the words are never silently invisible.
    refuse('foreign_envelope_version', parsed.text, NO_FACTS, nowMs);
  }

  const streamKey = streamKeyFor(parsed);
  const previous = linesByStreamKey.get(streamKey) ?? null;
  // A silence gap starts a new line, exactly as before — and so does an
  // envelope whose SOURCE or TARGET differs from the current line's: text
  // from a different speaker, epoch or reader language must never fuse onto
  // another line's tail (the design review's keying requirement). A legacy
  // raw delta (all-null facts) keeps pure gap semantics against a legacy
  // line; against an envelope-keyed line it also starts fresh, because a
  // producer downgrade mid-line is a boundary, not a continuation.
  // The turn id joins the key (Sol, 2026-08-25): text from two turns fusing
  // onto one line would hand the LAST turn's id a caption containing the
  // PREVIOUS turn's words — the exact misattribution id-correlation exists
  // to kill. A producer flapping the stamp mid-turn fragments lines, which
  // is the honest failure (an attribution change IS a boundary).
  // STALENESS FENCES (design review r2, finding 3). A worker takeover or
  // republication can deliver a delayed stream after its successor: a delta
  // for the SAME source at a LOWER epoch is a superseded stream and must not
  // replace the current line; the same key at a LOWER route generation is a
  // stale route's tail and must not splice in; a regressed assignment
  // generation is a world that no longer exists. Legacy all-null facts fence
  // nothing — there is nothing attested to compare.
  // VALIDATE every fence FIRST, then advance them together (r5 finding 3):
  // advancing the route high-water before the assignment check let one stale
  // delta poison the fence and drop legitimate traffic for ever. And the
  // call-global assignment fence runs for EVERY envelope delta — a malformed
  // null-spid envelope must not bypass it (r5 finding 2).
  {
    const spid = parsed.source_participant_id;
    const isEnvelope =
      parsed.target_language !== null ||
      spid !== null ||
      parsed.source_turn_id !== null ||
      parsed.source_epoch !== null ||
      parsed.assignment_generation !== null ||
      parsed.route_generation !== null;
    if (isEnvelope) {
      // A fenced delta is PROVISIONAL text of a superseded stream (D9): it
      // may be replaced — with a counter and the words in the log, never
      // silently. The line it would have grown keeps every word it already
      // rendered (it was marked superseded when its successor was admitted).
      if (
        parsed.assignment_generation !== null &&
        assignmentHighWater !== null &&
        parsed.assignment_generation < assignmentHighWater
      ) {
        refuse('stale_assignment_generation', parsed.text, parsed, nowMs);
        return;
      }
      let seenEpoch: number | undefined;
      let routeKey: string | null = null;
      let seenRoute: number | undefined;
      if (spid !== null && parsed.source_epoch !== null) {
        seenEpoch = epochHighWaterBySource.get(spid);
        if (seenEpoch !== undefined && parsed.source_epoch < seenEpoch) {
          refuse('stale_epoch', parsed.text, parsed, nowMs);
          return;
        }
        if (parsed.route_generation !== null) {
          routeKey = `${spid}\u0000${parsed.source_epoch}`;
          seenRoute = routeGenHighWater.get(routeKey);
          if (seenRoute !== undefined && parsed.route_generation < seenRoute) {
            refuse('stale_route_generation', parsed.text, parsed, nowMs);
            return;
          }
        }
      }
      // Every fence passed — advance them all.
      if (parsed.assignment_generation !== null) {
        assignmentHighWater = Math.max(assignmentHighWater ?? 0, parsed.assignment_generation);
      }
      if (spid !== null && parsed.source_epoch !== null) {
        if (epochHighWaterBySource.size < FENCE_MAP_CAP || seenEpoch !== undefined) {
          epochHighWaterBySource.set(spid, Math.max(seenEpoch ?? 0, parsed.source_epoch));
        }
        if (routeKey !== null && parsed.route_generation !== null) {
          if (routeGenHighWater.size < FENCE_MAP_CAP * 4 || seenRoute !== undefined) {
            routeGenHighWater.set(
              routeKey,
              Math.max(seenRoute ?? 0, parsed.route_generation),
            );
          }
        }
      }
    }
  }
  // D3 (TL-05): when both the line and the delta carry an engine offset, the
  // delta must continue EXACTLY where the line ends. Anything else is a
  // different place on the stream — a boundary, never a splice. The old line
  // keeps its words (rule 120 / D9) and the discontinuity is counted.
  let offsetDiscontinuity = false;
  if (
    previous !== null &&
    previous.engine_offset !== null &&
    parsed.engine_offset !== null &&
    parsed.engine_offset !== previous.engine_offset + codePointLength(previous.text)
  ) {
    offsetDiscontinuity = true;
    offsetCounts.offset_discontinuity += 1;
    logger.error('liveTranslatedCaptions', 'caption delta is not contiguous with its line; a new line starts', {
      line_origin: previous.engine_offset,
      line_end: previous.engine_offset + codePointLength(previous.text),
      delta_offset: parsed.engine_offset,
      text: parsed.text,
      count: offsetCounts.offset_discontinuity,
    });
    refuse('display_boundary', parsed.text, parsed, nowMs);
  }
  const startsNewCaption =
    previous === null ||
    offsetDiscontinuity ||
    nowMs - previous.lastUpdateMs > LIVE_TRANSLATION_TURN_GAP_MS;

  let next: LiveTranslatedCaption;
  if (startsNewCaption) {
    if (parsed.text.trim() === '') return;
    if (previous !== null) {
      // A silence gap starts a new caption; the previous one was RENDERED,
      // so its words are kept and marked (rule 120 / D9) — never overwritten
      // under the same key.
      supersedeLine(streamKey, previous);
    }
    captionCounter += 1;
    next = {
      id: `lt-${captionCounter}`,
      text: parsed.text,
      total_chars: parsed.text.length,
      lastUpdateMs: nowMs,
      target_language: parsed.target_language,
      source_language: parsed.source_language,
      source_participant_id: parsed.source_participant_id,
      source_turn_id: parsed.source_turn_id,
      source_epoch: parsed.source_epoch,
      assignment_generation: parsed.assignment_generation,
      route_generation: parsed.route_generation,
      superseded: false,
      engine_offset: parsed.engine_offset,
    };
  } else {
    next = {
      ...previous,
      text: previous.text + parsed.text,
      total_chars: previous.total_chars + parsed.text.length,
      lastUpdateMs: nowMs,
      // Generations may advance mid-line without a key change; keep the newest.
      assignment_generation: parsed.assignment_generation ?? previous.assignment_generation,
      route_generation: parsed.route_generation ?? previous.route_generation,
    };
  }

  admitLine(streamKey, next, nowMs);
  emit();
}

/**
 * Forget the current caption. Called from Conference's room teardown so a
 * finished call's text can never flash onto the next call's overlay in the
 * same tab.
 */
export function resetLiveTranslatedCaption(): void {
  epochHighWaterBySource.clear();
  routeGenHighWater.clear();
  assignmentHighWater = null;
  refusalCounts = zeroRefusalCounts();
  refusalLog = [];
  offsetCounts = { offset_malformed: 0, offset_discontinuity: 0, legacy_text_placement: 0 };
  legacyPlacementCounted.clear();
  if (current === null && linesByStreamKey.size === 0) return;
  linesByStreamKey.clear();
  current = null;
  currentLines = Object.freeze([]);
  emit();
}

/**
 * A DISPLAY BOUNDARY (the reader-language switch; also the ingest handler's
 * cleanup and a reconnect): every live line is marked superseded and its
 * words are KEPT (rule 120 / D9 — they were rendered); every staleness fence
 * survives (r5 finding 4).
 *
 * The reader-language switch is a display boundary, not a call boundary:
 * pre-switch and post-switch text must not concatenate — a superseded line's
 * key is released, so the next delta starts a new line — and a delayed
 * lower-epoch stream is exactly as stale after the switch as before it,
 * because the high-waters are untouched. The overlay's attested-target guard
 * keeps a superseded line of another reader language off the new label.
 * Before D9 this DELETED every line silently.
 */
export function clearLiveTranslatedCaptionLine(nowMs: number = Date.now()): void {
  if (current === null && linesByStreamKey.size === 0) return;
  const live = Array.from(linesByStreamKey.entries()).filter(([, line]) => !line.superseded);
  for (const [key, line] of live) {
    supersedeLine(key, line);
    refuse(
      'display_boundary',
      line.text,
      {
        source_participant_id: line.source_participant_id,
        source_epoch: line.source_epoch,
        target_language: line.target_language,
      },
      nowMs,
    );
  }
  current = null;
  currentLines = Object.freeze(
    Array.from(linesByStreamKey.values()).sort(
      (left, right) =>
        right.lastUpdateMs - left.lastUpdateMs || right.id.localeCompare(left.id),
    ),
  );
  emit();
}

/** All per-source rolling captions, newest first. */
export function useLiveTranslatedCaptions(): readonly LiveTranslatedCaption[] {
  return useSyncExternalStore(
    subscribeLiveTranslatedCaption,
    getLiveTranslatedCaptionsSnapshot,
    getServerLinesSnapshot,
  );
}
